import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import styles from './Table.module.css';
import ConfirmDialog from '../components/ConfirmDialog';

const SERVER_URL =
  process.env.NODE_ENV === 'production'
    ? window.location.origin
    : 'http://localhost:3000';

const imgUrl = (p) =>
  p ? `${SERVER_URL}${p.startsWith('/') ? p : `/${p}`}` : '';

const emptyEdit = {
  title: '',
  brand: '',
  price: '',
  description: '',
  city: '',
  area: '',
  address: '',
  state: '',
  categoryId: '',
  subcategoryId: '',
};

function formatPrice(n) {
  const v = Number(n) || 0;
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(v % 1e7 === 0 ? 0 : 1)} Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(v % 1e5 === 0 ? 0 : 1)} L`;
  return `₹${v.toLocaleString('en-IN')}`;
}

function expiryLabel(expiresAt) {
  if (!expiresAt) return { text: '—', color: '#888' };
  const exp = new Date(expiresAt);
  const daysLeft = Math.ceil((exp - Date.now()) / 86400000);
  if (daysLeft < 0) return { text: 'Expired', color: '#E8192C' };
  if (daysLeft <= 5) return { text: `${daysLeft}d left`, color: '#e6a817' };
  return { text: `${daysLeft}d left`, color: '#555' };
}

export default function Ads() {
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [confirm, setConfirm] = useState(null);
  const [editAd, setEditAd] = useState(null);
  const [editForm, setEditForm] = useState(emptyEdit);
  const [editError, setEditError] = useState('');
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api
      .get('/categories')
      .then((r) => setCategories(r.data || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!editForm.categoryId) {
      setSubcategories([]);
      return;
    }
    api
      .get(`/subcategories?categoryId=${editForm.categoryId}`)
      .then((r) => setSubcategories(r.data || []))
      .catch(() => setSubcategories([]));
  }, [editForm.categoryId]);

  const load = useCallback(
    async (p = page) => {
      setLoading(true);
      try {
        const params = new URLSearchParams({ page: p, limit: 20 });
        if (search) params.set('q', search);
        if (filter === 'pending') params.set('isApproved', 'false');
        if (filter === 'active') params.set('isActive', 'true');
        if (filter === 'inactive') params.set('isActive', 'false');
        if (filter === 'featured') params.set('featured', 'true');
        const { data } = await api.get(`/ads/admin/all?${params}`);
        setAds(data.ads);
        setTotal(data.total);
        setPages(data.pages);
        setPage(p);
      } catch {
        alert('Failed to load ads');
      } finally {
        setLoading(false);
      }
    },
    [page, search, filter]
  );

  useEffect(() => {
    load(1);
  }, [search, filter]); // eslint-disable-line

  const toggle = async (ad) => {
    const action = ad.isActive ? 'Deactivate' : 'Activate';
    setConfirm({
      message: `${action} "${ad.title}"?`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await api.patch(`/ads/admin/${ad._id}/toggle`);
          load(page);
        } catch {
          alert('Failed');
        }
      },
    });
  };

  const approve = async (ad, action) => {
    const label = action === 'approve' ? 'Approve' : 'Disapprove';
    setConfirm({
      message: `${label} "${ad.title}"?`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await api.patch(`/ads/admin/${ad._id}/approve`, { action });
          load(page);
        } catch {
          alert('Failed');
        }
      },
    });
  };

  const remove = async (ad) => {
    setConfirm({
      message: `Delete "${ad.title}"? This cannot be undone.`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await api.delete(`/ads/admin/${ad._id}`);
          load(page);
        } catch {
          alert('Delete failed');
        }
      },
    });
  };

  const toggleFeatured = async (ad) => {
    const action = ad.isFeatured ? 'Unfeature' : 'Feature';
    setConfirm({
      message: `${action} "${ad.title}"?`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await api.patch(`/ads/admin/${ad._id}/feature`);
          load(page);
        } catch {
          alert('Failed');
        }
      },
    });
  };

  const openEdit = (ad) => {
    setEditAd(ad);
    setEditError('');
    setEditForm({
      title: ad.title || '',
      brand: ad.brand || '',
      price: ad.price != null ? String(ad.price) : '',
      description: ad.description || '',
      city: ad.city || '',
      area: ad.area || '',
      address: ad.address || '',
      state: ad.state || '',
      categoryId: ad.category?._id || ad.category || '',
      subcategoryId: ad.subcategory?._id || ad.subcategory || '',
    });
  };

  const closeEdit = () => {
    setEditAd(null);
    setEditForm(emptyEdit);
    setEditError('');
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editAd) return;
    if (!editForm.categoryId) {
      setEditError('Category is required');
      return;
    }
    setSaving(true);
    setEditError('');
    try {
      await api.put(`/ads/admin/${editAd._id}`, {
        title: editForm.title.trim(),
        brand: editForm.brand.trim(),
        price: editForm.price,
        description: editForm.description.trim(),
        city: editForm.city.trim(),
        area: editForm.area.trim(),
        address: editForm.address.trim(),
        state: editForm.state.trim(),
        category: editForm.categoryId,
        subcategory: editForm.subcategoryId || null,
      });
      closeEdit();
      load(page);
    } catch (err) {
      setEditError(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      {confirm && (
        <ConfirmDialog
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      <div className={styles.header}>
        <h2 className={styles.title}>
          Ads{' '}
          <span style={{ fontSize: 14, color: '#888', fontWeight: 400 }}>
            ({total})
          </span>
        </h2>
      </div>

      <div className={styles.adsToolbar}>
        <input
          className={styles.searchInput}
          placeholder="Search title or brand..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className={styles.filterGroup}>
          {['all', 'pending', 'active', 'inactive', 'featured'].map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={
                filter === f ? styles.filterBtnActive : styles.filterBtn
              }
            >
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className={styles.center}>Loading...</div>
      ) : (
        <>
          <div className={styles.tableWrap}>
            <table className={`${styles.table} ${styles.adsTable}`}>
              <thead>
                <tr>
                  <th>Ad</th>
                  <th>Category</th>
                  <th>Status</th>
                  <th>Meta</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {ads.length === 0 && (
                  <tr>
                    <td colSpan={5} className={styles.empty}>
                      No ads found
                    </td>
                  </tr>
                )}
                {ads.map((ad) => {
                  const exp = expiryLabel(ad.expiresAt);
                  const approval = ad.approvalStatus || 'pending';
                  return (
                    <tr key={ad._id}>
                      <td>
                        <div className={styles.adCell}>
                          <div className={styles.adThumbWrap}>
                            {ad.images?.[0] ? (
                              <img
                                src={imgUrl(ad.images[0])}
                                alt=""
                                className={styles.adThumb}
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  e.currentTarget.nextSibling.style.display =
                                    'flex';
                                }}
                              />
                            ) : null}
                            <div
                              className={styles.adThumbFallback}
                              style={{
                                display: ad.images?.[0] ? 'none' : 'flex',
                              }}
                            >
                              No img
                            </div>
                          </div>
                          <div className={styles.adInfo}>
                            <button
                              type="button"
                              className={styles.adTitleBtn}
                              title={ad.title}
                              onClick={() => navigate(`/ads/${ad._id}`)}
                            >
                              {ad.title}
                            </button>
                            <div className={styles.adPrice}>
                              {formatPrice(ad.price)}
                            </div>
                            <div className={styles.adSeller}>
                              {ad.user?.name || '—'}
                              {ad.user?.email ? ` · ${ad.user.email}` : ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className={styles.catName}>
                          {ad.category?.name || '—'}
                        </div>
                        {ad.subcategory?.name && (
                          <div className={styles.subCatName}>
                            {ad.subcategory.name}
                          </div>
                        )}
                      </td>
                      <td>
                        <div className={styles.statusStack}>
                          <button
                            type="button"
                            className={
                              ad.isActive
                                ? styles.badgeActive
                                : styles.badgeInactive
                            }
                            onClick={() => toggle(ad)}
                            title="Toggle active"
                          >
                            {ad.isActive ? 'Active' : 'Inactive'}
                          </button>
                          {approval === 'approved' ? (
                            <button
                              type="button"
                              className={styles.badgeApproved}
                              onClick={() => approve(ad, 'disapprove')}
                              title="Click to disapprove"
                            >
                              Approved
                            </button>
                          ) : approval === 'disapproved' ? (
                            <button
                              type="button"
                              className={styles.badgeInactive}
                              onClick={() => approve(ad, 'approve')}
                              title="Click to approve"
                            >
                              Disapproved
                            </button>
                          ) : (
                            <button
                              type="button"
                              className={styles.badgePending}
                              onClick={() => approve(ad, 'approve')}
                              title="Click to approve"
                            >
                              Pending
                            </button>
                          )}
                          <button
                            type="button"
                            className={
                              ad.isFeatured
                                ? styles.badgeFeatured
                                : styles.badgeMuted
                            }
                            onClick={() => toggleFeatured(ad)}
                            title="Toggle featured"
                          >
                            {ad.isFeatured ? '★ Featured' : '☆ Feature'}
                          </button>
                        </div>
                      </td>
                      <td>
                        <div className={styles.metaStack}>
                          <span>
                            {new Date(ad.createdAt).toLocaleDateString()}
                          </span>
                          <span>{ad.views ?? 0} views</span>
                          <span style={{ color: exp.color }}>{exp.text}</span>
                        </div>
                      </td>
                      <td>
                        <div className={styles.actions}>
                          <button
                            type="button"
                            className={styles.editBtn}
                            onClick={() => openEdit(ad)}
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            className={styles.deleteBtn}
                            onClick={() => remove(ad)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pages > 1 && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.filterBtn}
                disabled={page <= 1}
                onClick={() => load(page - 1)}
              >
                ← Prev
              </button>
              <span>
                Page {page} of {pages}
              </span>
              <button
                type="button"
                className={styles.filterBtn}
                disabled={page >= pages}
                onClick={() => load(page + 1)}
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {editAd && (
        <div className={styles.overlay} onClick={closeEdit}>
          <div
            className={styles.modal}
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: 520 }}
          >
            <h3>Edit Ad</h3>
            {editError && <div className={styles.formError}>{editError}</div>}
            <form onSubmit={saveEdit} className={styles.form}>
              <label>Category *</label>
              <select
                value={editForm.categoryId}
                onChange={(e) =>
                  setEditForm({
                    ...editForm,
                    categoryId: e.target.value,
                    subcategoryId: '',
                  })
                }
                required
              >
                <option value="">Select category</option>
                {categories.map((c) => (
                  <option key={c._id} value={c._id}>
                    {c.name}
                  </option>
                ))}
              </select>
              <label>Subcategory</label>
              <select
                value={editForm.subcategoryId}
                onChange={(e) =>
                  setEditForm({ ...editForm, subcategoryId: e.target.value })
                }
                disabled={!editForm.categoryId}
              >
                <option value="">None</option>
                {subcategories.map((s) => (
                  <option key={s._id} value={s._id}>
                    {s.name}
                  </option>
                ))}
              </select>
              <label>Title *</label>
              <input
                value={editForm.title}
                onChange={(e) =>
                  setEditForm({ ...editForm, title: e.target.value })
                }
                required
              />
              <label>Brand</label>
              <input
                value={editForm.brand}
                onChange={(e) =>
                  setEditForm({ ...editForm, brand: e.target.value })
                }
              />
              <label>Price (₹) *</label>
              <input
                type="number"
                min="0"
                step="any"
                value={editForm.price}
                onChange={(e) =>
                  setEditForm({ ...editForm, price: e.target.value })
                }
                required
              />
              <label>Description *</label>
              <textarea
                value={editForm.description}
                onChange={(e) =>
                  setEditForm({ ...editForm, description: e.target.value })
                }
                rows={4}
                required
              />
              <div className={styles.formRow2}>
                <div>
                  <label>City</label>
                  <input
                    value={editForm.city}
                    onChange={(e) =>
                      setEditForm({ ...editForm, city: e.target.value })
                    }
                  />
                </div>
                <div>
                  <label>Area</label>
                  <input
                    value={editForm.area}
                    onChange={(e) =>
                      setEditForm({ ...editForm, area: e.target.value })
                    }
                  />
                </div>
              </div>
              <label>Address</label>
              <input
                value={editForm.address}
                onChange={(e) =>
                  setEditForm({ ...editForm, address: e.target.value })
                }
              />
              <label>State</label>
              <input
                value={editForm.state}
                onChange={(e) =>
                  setEditForm({ ...editForm, state: e.target.value })
                }
              />
              <div className={styles.modalActions}>
                <button
                  type="button"
                  className={styles.cancelBtn}
                  onClick={closeEdit}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className={styles.saveBtn}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
